/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-useless-computed-key */
/* eslint-disable no-unused-expressions */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";

import BrokingSummary from "components/BrokingSummary";
import Button, { ButtonContainer } from "components/Button";
import ContentCard from "components/ContentCard";
import FormContainer from "components/FormContainer";
import Layout from "components/Layout";
import Loader from "components/Loader";
import RenderButtons from "components/RenderButtons";
import HasPermission from "components/HasPermission";
import { LostCancelButton } from "components/index";
import StepNavigation from "components/StepNavigation/index";
import Colors from "constants/Colors";
import { useOperationInfo } from "services/OperationService";
import { useGenerateWording, useGetEditWordingUrl, useHeaderNbi, useInsurerTaxes, useMrc1, useMrc2, useScheduleReport } from "services/QuoteService";
import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import { FitinsurAPI, MessageCenter } from "utils";
import { ButtonContent } from "./styles";
import PremiumCalculationSection from "components/PremiumCalculationSection/index";
import { SECTIONS } from "./utils";

import {
  useDeleteQuestionnaireAnswer,
  useGetQuestionnaireAnswer,
  useSaveQuestionnaireAnswerSync
} from "services/QuestionnaireAnswerService";

import {
  useDeleteAllDocumentsByIds
} from "@fitinsur/services/DocumentsService";

import {
  GeneralSection,
  DeclarationSection,
  MRC1AttachmentsSection,
  MRC1Section,
  MRC2AttachmentsSection,
  MRC2Section,
  PolicyWordingSection,
  PremiumScheduleAttachmentsSection,
  PremiumScheduleSection,
  OtherDocumentsSection
} from "./sections/index";

export default function ContractBuilder(props) {
  const authContext = useContext(GlobalAuthContext);
  const loading = useContext(GlobalLoaderContext);

  const mc = new MessageCenter();
  const fitinsur = new FitinsurAPI();

  const history = useHistory();
  const formikRef = useRef(Formik);
  const refQuestionnaireSent = useRef(false);

  const [summary, setSummary] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [questionnaireAnswersToDelete, setQuestionnaireAnswersToDelete] = useState([]);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);

  const [isBound, setIsBound] = useState(false);
  const [operation, setOperation] = useState(null);
  const [includeDeclaration, setIncludeDeclaration] = useState(true);

  const [generateMRC1, setGenerateMRC1] = useState(false);
  const [generateMRC2, setGenerateMRC2] = useState(true);
  const [generatePremiumSchedule, setGeneratePremiumSchedule] = useState(false);
  const [endorsementType, setEndorsementType] = useState("");
  const [loadingPack, setLoadingPack] = useState(false);

  const [isChecking, setIsChecking] = useState(false);

  const [editUrl, setEditUrl] = useState("");

  const {
    request: useHeaderNbiRequest,
  } = useHeaderNbi();

  const {
    data: useGetQuestionnaireAnswersData,
    loading: useGetQuestionnaireAnswersLoading,
    request: useGetQuestionnaireAnswersRequest,
    status: useGetQuestionnaireAnswersStatus,
  } = useGetQuestionnaireAnswer(props.match.params.idOperation);

  const {
    loading: useSaveQuestionnaireAnswerLoading,
    status: useSaveQuestionnaireAnswerStatus,
    request: useSaveQuestionnaireAnswerRequest,
  } = useSaveQuestionnaireAnswerSync(props.match.params.idOperation);

  const {
    loading: useDeleteQuestionnaireAnswerLoading,
    request: useDeleteQuestionnaireAnswerRequest,
  } = useDeleteQuestionnaireAnswer(props.match.params.idOperation);

  const {
    loading: useDeleteAllDocumentsByIdLoading,
    request: useDeleteAllDocumentsByIdsRequest,
  } = useDeleteAllDocumentsByIds(props.match.params.idOperation);

  const {
    loading: useInsurerTaxesLoading,
    request: useInsurerTaxesRequest,
  } = useInsurerTaxes(props.match.params.idOperation);

  const {
    loading: useOperationInfoLoading,
    request: useOperationInfoRequest,
  } = useOperationInfo(props.match.params.idOperation);

  const {
    loading: useMrc1Loading,
    request: useMrc1Request,
  } = useMrc1(props.match.params.idOperation);

  const {
    loading: useMrc2Loading,
    request: useMrc2Request,
  } = useMrc2(props.match.params.idOperation);

  const {
    loading: useGetEditWordingUrlLoading,
    request: useGetEditWordingUrlRequest,
  } = useGetEditWordingUrl(props.match.params.idOperation);

  const {
    loading: useGenerateWordingLoading,
    request: useGenerateWordingRequest,
  } = useGenerateWording(props.match.params.idOperation);

  const {
    loading: useScheduleReportLoading,
    request: useScheduleReportRequest,
  } = useScheduleReport(props.match.params.idOperation);

  useEffect(() => {
    if (
      useGetQuestionnaireAnswersLoading ||
      useSaveQuestionnaireAnswerLoading ||
      useDeleteQuestionnaireAnswerLoading ||
      useDeleteAllDocumentsByIdLoading ||
      useInsurerTaxesLoading ||
      useOperationInfoLoading ||
      useMrc1Loading ||
      useMrc2Loading ||
      useGetEditWordingUrlLoading ||
      useGenerateWordingLoading ||
      isChecking ||
      useScheduleReportLoading ||
      loadingPack
    ) {
      loading.start();
    } else {
      loading.stop();
    }
  }, [
    useGetQuestionnaireAnswersLoading,
    useSaveQuestionnaireAnswerLoading,
    useDeleteQuestionnaireAnswerLoading,
    useDeleteAllDocumentsByIdLoading,
    useInsurerTaxesLoading,
    useOperationInfoLoading,
    useMrc1Loading,
    useMrc2Loading,
    useGetEditWordingUrlLoading,
    useGenerateWordingLoading,
    isChecking,
    useScheduleReportLoading,
    loadingPack
  ]);

  useEffect(() => {
    fitinsur.setAuthToken(authContext.token);
    getInsurerTaxes();
    getOperationInfo();
    handleSummary();

    useGetQuestionnaireAnswersRequest();
  }, []);

  function handleSummary() {
    useHeaderNbiRequest({ idOperation: props.match.params.idOperation }, null)
      .then(({ data }) => {
        setSummary({
          typeOfCover: data.ret_value.typeOfCover,
          client: data.ret_value.client,
          responseBy: data.ret_value.responseBy,
          internalContact: data.ret_value.internalContact,
          broker: data.ret_value.broker,
          dealCode: data.ret_value.deal,
          nmFacility: data.ret_value.nmFacility,
          currency: data.ret_value.currency,
          nmObligor: data.ret_value.nmObligor,
          projectName: data.ret_value.projectName,
          idOperation: props.match.params.idOperation,
        });
        setEndorsementType(data.ret_value.endorsmentType);
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleSummary.name}\n` + error);
      });
  }

  const getInsurers = () => {
    const allInsurers = facilities.flatMap((facility) => facility.insurers).filter(ins => ins.lineSize);
    const uniqueInsurers = {};
    allInsurers.forEach((insurer) => {
      const key = `${insurer.nmInsurer}-${insurer.idPersonInsurer}`;
      if (!uniqueInsurers[key]) {
        uniqueInsurers[key] = { nmInsurer: insurer.nmInsurer, idPersonInsurer: insurer.idPersonInsurer };
      }
    })
    return Object.values(uniqueInsurers);
  }

  function handleQuestionnaire(data) {
    let questionnaires = [
      ...data.mrc,
      ...data.persons,
      ...data.operations,
      ...data.risks,
    ].sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

    const insurers = getInsurers();

    const isLloydsOrLima = _isLloydsOrLima();

    const { idProduct } = operation;

    questionnaires = GeneralSection.events.handleInitialAdjustments({ questionnaire: questionnaires });

    questionnaires = PolicyWordingSection.events.handleInitialAdjustments({ questionnaire: questionnaires, idProduct })

    questionnaires = MRC1Section.events.handleInitialAdjustments({ questionnaire: questionnaires, idProduct, isLloydsOrLima, setGenerateMRC1 });

    questionnaires = MRC1AttachmentsSection.events.handleInitialAdjustments(questionnaires);

    questionnaires = MRC2Section.events.handleInitialAdjustments({
      questionnaire: questionnaires,
      isLloydsOrLima,
      idProduct,
      setGenerateMRC2
    })

    questionnaires = MRC2AttachmentsSection.events.handleInitialAdjustments({ questionnaire: questionnaires, insurers })

    questionnaires = PremiumScheduleSection.events.handleInitialAdjustments({ questionnaire: questionnaires, setGeneratePremiumSchedule })

    questionnaires = PremiumScheduleAttachmentsSection.events.handleInitialAdjustments({ questionnaire: questionnaires, insurers })

    questionnaires = DeclarationSection.events.handleInitialAdjustments({ questionnaire: questionnaires, setIncludeDeclaration })

    setQuestionnaire(questionnaires);
  }

  useEffect(() => {
    if (useGetQuestionnaireAnswersStatus === 200 && !!operation && !!operation.idOperation && !!facilities && !!facilities.length) {
      handleQuestionnaire(useGetQuestionnaireAnswersData?.value);
    }
  }, [useGetQuestionnaireAnswersStatus, facilities, operation]);

  async function handlePolicyPack() {
    setLoadingPack(true)
    fitinsur.setAuthToken(authContext.token);
    fitinsur
      .createPolicyPack(props.match.params.idOperation)
      .then((resp) => resp.json())
      .then((data) => {
        if (data.ret_code !== 200) {
          mc.error(data.ret_msg_details);
          setLoadingPack(false);
          return;
        }
        setLoadingPack(false);

        history.push(`/broking/send-approval-pack/${props.match.params.idOperation}`);

      })
      .catch((error) => {
        setLoadingPack(false);
        mc.error(error);
        console.error("fitPostReject - Error:", error);
      });
  }

  function getStatusOperation() {
    setIsChecking(true);
    fitinsur.setAuthToken(authContext.token);
    fitinsur
      .requestGenerateMrcStatus(props.match.params.idOperation)
      .then((response) => response.json())
      .then((data) => {
        if (data.ret_code !== 200) {
          mc.error(data.ret_msg_details || "There was an error while processing your data, please try again");
          setIsChecking(false);
          return;
        }

        if (data.ret_value.errorMessage && data.ret_value.errorMessage.trim().length) {
          mc.error(data.ret_value.errorMessage || "There was an error while processing your data, please try again");
          setIsChecking(false);
          return;
        }

        if (data.ret_value.statusOperation === "created") {
          handlePolicyPack();
          return;
        }
        setTimeout(() => {
          getStatusOperation();
        }, 3000);
      })
      .catch((error) => {
        setIsChecking(false);

        mc.error("There was an error while fetching your data:  \n" + error);
        console.error("fitPostListContact - Error:", error);
      });
  }

  const generateDraftPackDocuments = async () => {
    fitinsur.setAuthToken(authContext.token);
    try {
      const signatureDocResponse = await useMrc1Request({ signatureDocumentOnly: true })
      if (signatureDocResponse.status !== 200) {
        mc.error(signatureDocResponse.ret_msg_details);
        return;
      }

      if (generatePremiumSchedule) {
        const scheduleReportResponse = await useScheduleReportRequest()
        if (scheduleReportResponse.status !== 200) {
          mc.error(scheduleReportResponse.ret_msg_details);
          return
        }
      }

      if (generateMRC1) {
        const mrc1RequestResponse = await useMrc1Request({})
        if (mrc1RequestResponse.status !== 200) {
          mc.error(mrc1RequestResponse.ret_msg_details);
          return;
        }
      }

      if (generateMRC2) {
        const mrc2RequestResponse = await useMrc2Request({ isMarketChanges: endorsementType === 'market-changes' })
        if (mrc2RequestResponse.status !== 200) {
          mc.error(mrc2RequestResponse.ret_msg_details);
          return;
        }
        getStatusOperation()
      } else {
        handlePolicyPack();
      }
    } catch (error) {
      console.log("error mrc", error)
      mc.error("There was an error while saving your data");
      loading.stop();
    };
  }

  const getInsurerTaxes = async () => {
    useInsurerTaxesRequest()
      .then(({ data }) => {
        setFacilities(data.ret_value.facilities);
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${getInsurerTaxes.name}\n` + error);
      });
  };

  const getOperationInfo = async () => {
    useOperationInfoRequest()
      .then(({ data: response }) => {
        setOperation(response)
        setIsBound(response.isBound || false);

        if (response.idProduct === 16 && !response.isBound) {
          getEditWordingUrl();
        };
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${getOperationInfo.name}\n` + error);
      });
  };

  const _isLloydsOrLima = () => {
    let isLloydsOrLima = facilities
      .flatMap((fac) => fac.insurers)
      .filter(ins => ins.lineSize)
      .filter((ins) => ins.personInsurerLegalEntity?.lloydsType || ins.personInsurerLegalEntity?.lirmaType).length;
    return !!isLloydsOrLima;
  };

  const getEditWordingUrl = async () => {
    useGetEditWordingUrlRequest().then(({ data: resp }) => {
      if (resp.ret_code !== 200) {
        mc.error(resp.ret_msg_details);
        return;
      }
      setEditUrl(`${resp.ret_value}?renderComponents=true&displayHighlightInPdf=true`);
    }).catch((error) => {
        mc.error(`There was an error while fetching your data: ${getEditWordingUrl.name}\n` + error);
        console.error('fitPostFiles - Error:', error);
    });
  }

  const handleEditWording = async () => {
    window.open(editUrl, "_blank");
  };

  return (
    <Layout
      idOperation={props.match.params.idOperation}
      title={summary.projectName || summary.nmObligor || "Contract Builder"}
      subtitle={summary.dealCode || "Contract Builder"}
    >
      <StepNavigation stage={2} idOperation={props.match.params.idOperation} ignoreOperationRoot={false} />

      <BrokingSummary data={summary} />

      <ContentCard
        icon
        title="Contract Builder"
        info={{
          idOperation: props.match.params.idOperation || null,
          idProduct: props.location?.state?.idProduct || null,
        }}
      >
        <Formik
          initialValues={{ showDragZoneText: true, lock: false, translateText: true, autoUpload: true }}
          innerRef={formikRef}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize={true}
          render={(formikProps) => (
            <Form>
              <FormContainer title={"Policy Wording / Policy"} show={true}>
                <>
                  <PolicyWordingSection.components.RenderSection
                    disableAll={isBound}
                    formikProps={formikProps}
                    formikRef={formikRef}
                    idOperation={props.match?.params?.idOperation}
                    idOperationParent={props.match?.params?.idOperationParent}
                    idProduct={operation?.idProduct}
                    globalQuestionnaire={questionnaire}
                    setGlobalQuestionnaire={setQuestionnaire}
                    questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                    setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                    documentsToDelete={documentsToDelete}
                    setDocumentsToDelete={setDocumentsToDelete}
                    authContext={authContext}
                  />
                </>
              </FormContainer>

              <FormContainer title={"MRC"} show={true}>
                <MRC1Section.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                  setGenerateMRC1={setGenerateMRC1}
                />

                <MRC1AttachmentsSection.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                />

                <MRC2Section.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  setGenerateMRC2={setGenerateMRC2}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                />

                <MRC2AttachmentsSection.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                />
              </FormContainer>

              {operation?.idProduct === 16 && (
                <FormContainer title={"DECLARATION"} show={true}>
                  <DeclarationSection.components.RenderSection
                    disableAll={isBound}
                    formikProps={formikProps}
                    formikRef={formikRef}
                    idOperation={props.match?.params?.idOperation}
                    idOperationParent={props.match?.params?.idOperationParent}
                    idProduct={operation?.idProduct}
                    globalQuestionnaire={questionnaire}
                    setGlobalQuestionnaire={setQuestionnaire}
                    questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                    setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                    documentsToDelete={documentsToDelete}
                    setDocumentsToDelete={setDocumentsToDelete}
                    includeDeclaration={includeDeclaration}
                    setIncludeDeclaration={setIncludeDeclaration}
                    handleEditWording={handleEditWording}
                  />
                </FormContainer>
              )}

              <FormContainer title={"Premium Schedule"} show={true}>
                <PremiumScheduleSection.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  setGeneratePremiumSchedule={setGeneratePremiumSchedule}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                />

                <PremiumScheduleAttachmentsSection.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                />
              </FormContainer>

              <PremiumCalculationSection
                disableAll={isBound}
                formikRef={formikRef}
                questionnaire={questionnaire}
                setQuestionnaire={setQuestionnaire}
                values={formikProps.values}
                errors={formikProps.errors}
                setFieldValue={formikProps.setFieldValue}
                setFieldError={formikProps.setFieldError}
                idOperation={props.match.params.idOperation}
                idOperationParent={props.match.params.idOperationParent}
                path={props?.path}
                permissions={props.routePermissions}
                useGetQuestionnaireAnswerRequest={useGetQuestionnaireAnswersRequest}
                useSaveQuestionnaireAnswerRequest={useSaveQuestionnaireAnswerRequest}
                questionsIdsToSave={[1184, 1941, 1942, 1943, 1944, 1945, 1946, 1947, 1950, 1954, 1961, 1962, 1977, 1979]}
              />

              <FormContainer title={"Other Documents"} show={true}>
                <OtherDocumentsSection.components.RenderSection
                  disableAll={isBound}
                  formikProps={formikProps}
                  formikRef={formikRef}
                  idOperation={props.match?.params?.idOperation}
                  idOperationParent={props.match?.params?.idOperationParent}
                  idProduct={operation?.idProduct}
                  globalQuestionnaire={questionnaire}
                  setGlobalQuestionnaire={setQuestionnaire}
                  questionnaireAnswersToDelete={questionnaireAnswersToDelete}
                  setQuestionnaireAnswersToDelete={setQuestionnaireAnswersToDelete}
                  documentsToDelete={documentsToDelete}
                  setDocumentsToDelete={setDocumentsToDelete}
                />
              </FormContainer>

              <ButtonContainer>
                <Button style={{ padding: "0.75rem 0" }} forth onClick={() => history.goBack()}>
                  &lt; Back
                </Button>

                <RenderButtons idOperation={props.match.params.idOperation}>
                  <ButtonContent>
                    <HasPermission permission={!isBound}>
                      <div style={{ marginTop: "5px" }}>
                        <LostCancelButton idOperation={props.match?.params?.idOperation} />
                      </div>
                    </HasPermission>
                    <Button third style={{ marginLeft: "10px", marginTop: "5px" }} onClick={() => history.push(`/broking/market-composition/${props.match.params.idOperation}`)}>
                      CDR
                    </Button>

                    <HasPermission permission={!isBound}>
                      <Button
                        primary
                        style={{ marginLeft: "10px", marginTop: "5px" }}
                        onClick={() => GeneralSection.events.handleSave({
                          values: formikProps.values,
                          questionnaire,
                          idOperation: props.match?.params?.idOperation,
                          idOperationParent: props.match?.params?.idOperationParent,
                          idProduct: operation?.idProduct,
                          SECTIONS,
                          isDraft: false,
                          mc,
                          formikRef,
                          refQuestionnaireSent,
                          useGetQuestionnaireAnswersRequest,
                          useSaveQuestionnaireAnswerRequest,
                          questionnaireAnswersToDelete,
                          useDeleteQuestionnaireAnswerRequest,
                          documentsToDelete,
                          useDeleteAllDocumentsByIdsRequest,
                          submitFunction: generateDraftPackDocuments,
                          fitinsurAPI: fitinsur,
                          authContext,
                          getInsurers
                        })}
                      >
                        DRAFT PACK
                      </Button>
                    </HasPermission>
                  </ButtonContent>
                </RenderButtons>
              </ButtonContainer>
            </Form>

          )}

        />
        {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
      </ContentCard>
    </Layout>
  );
}
